import React from "react";
import { Client } from "@/types";
import { EditIcon } from "@/components/icons";

interface ClientSettingsSectionProps {
  client: Client;
  onEdit?: () => void;
}

const BUSINESS_TYPE_LABELS: Record<string, string> = {
  hospitality: "Hospitality",
  food_drink: "Food & drink",
  health_beauty: "Health & beauty",
  wellness: "Wellness",
  real_estate: "Real estate",
  events: "Events",
  tours_experiences: "Tours & experiences",
  retail: "Retail",
  "e-commerce": "E-commerce",
  services: "Services",
  b2b: "B2B",
  content_creator: "Content creator",
  other: "Other",
};

const SERVICE_AREA_LABELS: Record<string, string> = {
  local: "Local",
  regional: "Regional",
  national: "National",
  online: "Online",
};

const BOOST_STRATEGY_LABELS: Record<string, string> = {
  conservative: "Conservative",
  moderate: "Moderate",
  aggressive: "Aggressive (auto-execute)",
};

export const ClientSettingsSection: React.FC<ClientSettingsSectionProps> = ({
  client,
  onEdit,
}) => {
  const businessTypeLabel = client.businessType
    ? BUSINESS_TYPE_LABELS[client.businessType] || client.businessType
    : "Not set";
  const serviceAreaLabel = client.serviceArea
    ? SERVICE_AREA_LABELS[client.serviceArea] || client.serviceArea
    : "Not set";
  const boostStrategyLabel = client.boostStrategy
    ? BOOST_STRATEGY_LABELS[client.boostStrategy] || client.boostStrategy
    : "Not set";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">Client Settings</h3>
        {onEdit && (
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            <EditIcon className="w-3.5 h-3.5" />
            Edit
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div>
          <span className="text-gray-500">Posts per Week</span>
          <p className="font-medium text-gray-900">{client.postsPerWeek ?? "Not set"}</p>
        </div>

        <div>
          <span className="text-gray-500">Auto-fetch Ideas</span>
          <p className="font-medium text-gray-900">
            {client.autoFetchIdeasEnabled ? "Enabled" : "Disabled"}
          </p>
        </div>

        <div>
          <span className="text-gray-500">Business Type</span>
          <p className="font-medium text-gray-900">{businessTypeLabel}</p>
        </div>

        <div>
          <span className="text-gray-500">Service Area</span>
          <p className="font-medium text-gray-900">{serviceAreaLabel}</p>
        </div>

        <div>
          <span className="text-gray-500">Monthly Ad Budget</span>
          <p className="font-medium text-gray-900">
            {client.monthlyAdBudgetDefault != null
              ? `€${client.monthlyAdBudgetDefault.toLocaleString()}`
              : "Not set"}
          </p>
        </div>

        <div>
          <span className="text-gray-500">Boost Strategy</span>
          <p className="font-medium text-gray-900">{boostStrategyLabel}</p>
        </div>

        <div>
          <span className="text-gray-500">Auto-boost</span>
          <p className="font-medium text-gray-900">
            {client.autoBoostEnabled ? "Enabled" : "Disabled"}
          </p>
        </div>
      </div>
    </div>
  );
};
